import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Spinner } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaWallet,
  FaArrowDown,
  FaArrowLeft,
  FaExchangeAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import useWallet from '../hooks/useWallet';
import useOtcMarketplace from '../hooks/useOtcMarketplace';
import './OtcSwap.css';

const OtcSwap = () => {
  const { mode } = useParams(); // 'buy' or 'sell'
  const navigate = useNavigate();

  const {
    address,
    signer,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    formatAddress,
    isCorrectNetwork,
    switchToSonic,
  } = useWallet();
  const {
    activeBuyPhase,
    activeSellPhase,
    userBalances,
    fetchUserBalances,
    buy,
    sell,
    loading,
    error,
  } = useOtcMarketplace(signer);

  const [mcgpAmount, setMcgpAmount] = useState('');
  const [usdcAmount, setUsdcAmount] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);

  const isBuyMode = mode === 'buy';
  const activePhase = isBuyMode ? activeBuyPhase : activeSellPhase;
  const price = activePhase ? parseFloat(activePhase.priceFormatted) : 0;

  // Check for insufficient balance
  const hasInsufficientBalance = isBuyMode
    ? parseFloat(usdcAmount || '0') > parseFloat(userBalances.usdc || '0')
    : parseFloat(mcgpAmount || '0') > parseFloat(userBalances.mcgp || '0');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user balances when connected
  useEffect(() => {
    if (address) {
      fetchUserBalances(address);
    }
  }, [address, fetchUserBalances]);

  // Calculate the other field when one changes
  const calculateFromMcgp = useCallback((mcgpValue) => {
    if (!mcgpValue || parseFloat(mcgpValue) <= 0 || !price) {
      return '0';
    }
    const usdc = parseFloat(mcgpValue) * price;
    return usdc.toFixed(6);
  }, [price]);

  const calculateFromUsdc = useCallback((usdcValue) => {
    if (!usdcValue || parseFloat(usdcValue) <= 0 || !price) {
      return '0';
    }
    const mcgp = parseFloat(usdcValue) / price;
    return mcgp.toFixed(6);
  }, [price]);

  // Handle MCGP input change
  const handleMcgpChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setMcgpAmount(value);
      const calculatedUsdc = calculateFromMcgp(value);
      setUsdcAmount(value ? calculatedUsdc : '');
    }
  };

  // Handle USDC input change
  const handleUsdcChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setUsdcAmount(value);
      const calculatedMcgp = calculateFromUsdc(value);
      setMcgpAmount(value ? calculatedMcgp : '');
    }
  };

  // Handle MAX button click
  const handleMaxClick = (token) => {
    if (token === 'usdc') {
      const maxUsdc = userBalances.usdc;
      setUsdcAmount(maxUsdc);
      setMcgpAmount(calculateFromUsdc(maxUsdc));
    } else {
      const maxMcgp = userBalances.mcgp;
      setMcgpAmount(maxMcgp);
      setUsdcAmount(calculateFromMcgp(maxMcgp));
    }
  };

  const handleSwap = async () => {
    if (!isConnected) {
      toast.error('Please connect your wallet');
      return;
    }

    if (!isCorrectNetwork) {
      try {
        await switchToSonic();
      } catch {
        toast.error('Please switch to Sonic network');
        return;
      }
    }

    if (!mcgpAmount || parseFloat(mcgpAmount) <= 0) {
      toast.error('Please enter an amount');
      return;
    }

    setIsSwapping(true);
    try {
      if (isBuyMode) {
        await buy(mcgpAmount);
        toast.success(`Successfully bought ${mcgpAmount} MCGP!`);
      } else {
        await sell(mcgpAmount);
        toast.success(`Successfully sold ${mcgpAmount} MCGP!`);
      }
      setMcgpAmount('');
      setUsdcAmount('');
    } catch (err) {
      console.error('Swap error:', err);
      toast.error(err.reason || err.message || 'Swap failed');
    } finally {
      setIsSwapping(false);
    }
  };

  const formatBalance = (balance) => {
    const num = parseFloat(balance);
    if (isNaN(num)) return '0.00';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  return (
    <div className="otc-swap">
      <section className="swap-section">
        <Container>
          <div className="swap-container">
            <button className="back-btn" onClick={() => navigate('/otc')}>
              <FaArrowLeft /> Back
            </button>

            <div className="swap-card" data-aos="zoom-in">
              {/* Header */}
              <div className="swap-header">
                <h2>{isBuyMode ? 'Buy MCGP' : 'Sell MCGP'}</h2>
                {activePhase && (
                  <div className="active-phase">
                    <span className="phase-label">Active Phase:</span>
                    <span className="phase-name">{activePhase.name}</span>
                  </div>
                )}
              </div>

              {/* Connect Wallet Button */}
              {!isConnected && (
                <Button
                  className="connect-wallet-btn"
                  onClick={connect}
                  disabled={isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <FaWallet className="me-2" />
                      Connect Wallet
                    </>
                  )}
                </Button>
              )}

              {isConnected && !isCorrectNetwork && (
                <Button className="switch-network-btn" onClick={switchToSonic}>
                  Switch to Sonic Network
                </Button>
              )}

              {isConnected && isCorrectNetwork && (
                <>
                  <div className="connected-info">
                    <div className="wallet-address">
                      <FaWallet className="me-2" />
                      <span>{formatAddress(address)}</span>
                    </div>
                    <button className="disconnect-btn" onClick={disconnect}>
                      Disconnect
                    </button>
                  </div>

                  {/* Input Fields */}
                  <div className="swap-form">
                    <label className="input-label">
                      {isBuyMode ? 'Buy MCGP with USDC' : 'Sell MCGP for USDC'}
                    </label>

                    {isBuyMode ? (
                      <>
                        {/* Buy Mode: USDC (pay) -> MCGP (receive) */}
                        {/* USDC Input (what user pays) */}
                        <div className="token-input buy-input">
                          <div className="input-header">
                            <span className="token-symbol">USDC</span>
                            <span className="token-balance">
                              Balance: {formatBalance(userBalances.usdc)}
                            </span>
                          </div>
                          <div className="input-row">
                            <input
                              type="text"
                              value={usdcAmount}
                              onChange={handleUsdcChange}
                              placeholder="0.0"
                              className="amount-input"
                            />
                            <button className="max-btn" onClick={() => handleMaxClick('usdc')}>
                              MAX
                            </button>
                          </div>
                          <div className="input-footer">
                            <span className="usd-value">
                              ${formatBalance(usdcAmount || '0')}
                            </span>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="swap-arrow">
                          <FaArrowDown />
                        </div>

                        {/* MCGP Output (what user receives) */}
                        <div className="token-input output-field">
                          <div className="input-header">
                            <span className="token-symbol">MCGP</span>
                            <span className="token-balance">
                              Balance: {formatBalance(userBalances.mcgp)}
                            </span>
                          </div>
                          <div className="input-row">
                            <input
                              type="text"
                              value={mcgpAmount}
                              onChange={handleMcgpChange}
                              placeholder="0.0"
                              className="amount-input"
                            />
                          </div>
                          <div className="input-footer">
                            <span className="usd-value">
                              $
                              {mcgpAmount && price
                                ? (parseFloat(mcgpAmount) * price).toFixed(2)
                                : '0.00'}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Sell Mode: MCGP (pay) -> USDC (receive) */}
                        {/* MCGP Input (what user pays) */}
                        <div className="token-input sell-input">
                          <div className="input-header">
                            <span className="token-symbol">MCGP</span>
                            <span className="token-balance">
                              Balance: {formatBalance(userBalances.mcgp)}
                            </span>
                          </div>
                          <div className="input-row">
                            <input
                              type="text"
                              value={mcgpAmount}
                              onChange={handleMcgpChange}
                              placeholder="0.0"
                              className="amount-input"
                            />
                            <button className="max-btn" onClick={() => handleMaxClick('mcgp')}>
                              MAX
                            </button>
                          </div>
                          <div className="input-footer">
                            <span className="usd-value">
                              $
                              {mcgpAmount && price
                                ? (parseFloat(mcgpAmount) * price).toFixed(2)
                                : '0.00'}
                            </span>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="swap-arrow">
                          <FaArrowDown />
                        </div>

                        {/* USDC Output (what user receives) */}
                        <div className="token-input output-field">
                          <div className="input-header">
                            <span className="token-symbol">USDC</span>
                            <span className="token-balance">
                              Balance: {formatBalance(userBalances.usdc)}
                            </span>
                          </div>
                          <div className="input-row">
                            <input
                              type="text"
                              value={usdcAmount}
                              onChange={handleUsdcChange}
                              placeholder="0.0"
                              className="amount-input"
                            />
                          </div>
                          <div className="input-footer">
                            <span className="usd-value">
                              ${formatBalance(usdcAmount || '0')}
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Price Info */}
                    {activePhase && (
                      <div className="price-info">
                        <FaExchangeAlt className="me-2" />
                        <span>
                          Price (MCGP/USDC) = ${activePhase.priceFormatted}
                        </span>
                      </div>
                    )}

                    {/* Insufficient Balance Warning */}
                    {hasInsufficientBalance && (
                      <div className="swap-error">
                        Insufficient {isBuyMode ? 'USDC' : 'MCGP'} balance
                      </div>
                    )}

                    {/* Swap Button */}
                    <Button
                      className={`swap-btn ${
                        isBuyMode ? 'buy-btn' : 'sell-btn'
                      }`}
                      onClick={handleSwap}
                      disabled={
                        isSwapping ||
                        loading ||
                        !mcgpAmount ||
                        parseFloat(mcgpAmount) <= 0 ||
                        hasInsufficientBalance
                      }
                    >
                      {isSwapping || loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {isBuyMode ? 'Buying...' : 'Selling...'}
                        </>
                      ) : hasInsufficientBalance ? (
                        'Insufficient Balance'
                      ) : (
                        'Swap'
                      )}
                    </Button>

                    {error && <div className="swap-error">{error}</div>}
                  </div>
                </>
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default OtcSwap;
